import { money, fmtDate, pct, typeLabel, feeRule } from "@/lib/format";

const Metric = ({ label, value, sub, testid, strong }) => (
  <div className="min-w-0 px-5 py-5 sm:px-6" data-testid={testid}>
    <div className="eyebrow">{label}</div>
    <div className={`num mt-2 truncate ${strong ? "font-display text-2xl font-semibold sm:text-3xl" : "text-xl font-semibold"}`} data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export const LoanSummary = ({ loan, summary }) => {
  const s = summary;
  const nd = s.next_due;
  return (
    <div className="fade-up overflow-hidden rounded-lg border bg-card" data-testid="loan-summary">
      <div className="grid grid-cols-2 divide-x divide-y lg:grid-cols-4 lg:divide-y-0 [&>*]:border-border">
        <Metric testid="summary-payoff" label="Payoff amount" value={money(s.payoff_cents)} strong sub={`As of ${fmtDate(s.as_of)}`} />
        <Metric testid="summary-principal-balance" label="Principal balance" value={money(s.principal_balance_cents)} strong />
        <Metric testid="summary-accrued-interest" label="Accrued interest" value={money(s.accrued_interest_cents)}
          sub={loan.interest_type === "simple" ? `≈ ${money(Math.round(Number(s.daily_interest_cents_exact)))}/day · Actual/365` : "Prorated within current period"} />
        <Metric testid="summary-fees-outstanding" label="Outstanding fees" value={money(s.fees_outstanding_cents)} />
      </div>
      <div className="grid grid-cols-2 divide-x divide-y border-t lg:grid-cols-4 lg:divide-y-0 [&>*]:border-border">
        <Metric testid="summary-next-due" label="Next due" value={nd ? money(nd.amount_cents) : "—"} sub={nd ? `Installment ${nd.number} · ${fmtDate(nd.due_date)}` : s.status === "paid_off" ? `Paid off ${fmtDate(s.paid_off_date)}` : ""} />
        <Metric testid="summary-paid-principal" label="Principal paid" value={money(s.paid_principal_cents)} />
        <Metric testid="summary-paid-interest" label="Interest paid" value={money(s.paid_interest_cents)} />
        <Metric testid="summary-paid-fees" label="Fees paid" value={money(s.paid_fees_cents)}
          sub={s.past_due_cents > 0 ? `Past due installments: ${money(s.past_due_cents)}` : null} />
      </div>
      {s.overpayment_cents > 0 && (
        <div className="border-t bg-[hsl(var(--gold)/0.12)] px-6 py-3 text-sm font-semibold" data-testid="overpayment-flag">
          Overpayment {money(s.overpayment_cents)} — received in excess of the payoff amount
        </div>
      )}
    </div>
  );
};

export const LoanTerms = ({ loan, summary }) => {
  const rows = [
    ["Original principal", money(loan.principal_cents)],
    ["APR", pct(loan.apr)],
    ["Term", `${loan.term_months} months`],
    ["Method", typeLabel(loan.interest_type)],
    [loan.interest_type === "simple" ? "Scheduled monthly due" : "Monthly payment", money(summary.monthly_payment_cents)],
    ["Start date", fmtDate(loan.start_date)],
    ["First due date", fmtDate(loan.first_due_date)],
    ["Late fee", feeRule(loan)],
    ["Grace period", `${loan.grace_days} days`],
  ];
  return (
    <div className="fade-up rounded-lg border bg-card p-6" data-testid="loan-terms">
      <h3 className="text-lg font-semibold">Terms</h3>
      <dl className="mt-4 divide-y text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 py-2.5">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="num text-right font-medium" data-testid={`term-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
